'use client';

import Script from 'next/script';

import { useConsent } from './consent-context';

import RB2BLoader from '@/components/rb2b-loader';
import RedditPixel from '@/components/reddit-pixel';

/**
 * Every non-essential third-party script on the site, gated by consent
 * category. Nothing here renders until the visitor's effective consent allows
 * it: explicit choice, or the geo default (opt-out regions only), per
 * ConsentProvider. Consent Mode v2 defaults are set in RootDocument's inline
 * head script before any of these load.
 */

function AnalyticsScripts() {
  return (
    <>
      {/* Google Tag Manager */}
      <Script
        id="gtm-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-NBKDVG5Q');`,
        }}
      />

      {/* Google Analytics — two properties on purpose.
          G-5H9E72704L has been receiving this site's traffic; nobody currently
          knows who owns it or whether anyone still reads it. G-DBXC6NYX9G is
          the property the team can actually see, which means every event the
          demo form sends was landing somewhere invisible to them.
          Configuring both is the non-destructive answer: the old property
          keeps its history in case it matters, the new one starts receiving
          immediately. Drop G-5H9E72704L once someone confirms it is dead. */}
      <Script
        id="gtag-js"
        strategy="lazyOnload"
        src="https://www.googletagmanager.com/gtag/js?id=G-DBXC6NYX9G"
      />
      <Script
        id="gtag-init"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-DBXC6NYX9G');
              gtag('config', 'G-5H9E72704L');
            `,
        }}
      />

      {/* PostHog analytics (includes session replay — disclosed in the
          cookie policy under the analytics category) */}
      <Script
        id="posthog-tracking"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `!function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init Xr es pi Zr rs Kr Qr capture Ni calculateEventProperties os register register_once register_for_session unregister unregister_for_session ds getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey cancelPendingSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException startExceptionAutocapture stopExceptionAutocapture loadToolbar get_property getSessionProperty us ns createPersonProfile hs Vr vs opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing ss debug O ls getPageViewId captureTraceFeedback captureTraceMetric qr".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('phc_u9GgMbXv0x6UbIiSN1bTGmZUjNkM0dvOfp3mSIFih4n',{api_host:'https://eu.i.posthog.com',defaults:'2025-11-30',person_profiles:'identified_only'})`,
        }}
      />

      {/* HubSpot tracking */}
      <Script
        id="hs-script-loader"
        strategy="lazyOnload"
        src="https://js.hs-scripts.com/47743622.js"
      />

      {/* DataBuddy analytics */}
      <Script
        id="databuddy"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
              (function() {
                var s = document.createElement('script');
                s.src = 'https://cdn.databuddy.cc/databuddy.js';
                s.setAttribute('data-client-id', 'kCHb-WwNk6HsN5hLVUx-x');
                s.crossOrigin = 'anonymous';
                s.async = true;
                document.body.appendChild(s);
              })();
            `,
        }}
      />
    </>
  );
}

function MarketingScripts() {
  return (
    <>
      {/* Lemlist visitor tracking */}
      <Script
        id="lemlist-tracking"
        strategy="lazyOnload"
        src="https://app.lemlist.com/api/visitors/tracking?k=If8kZuopf8enk3n6m%2BByebH%2FFOjt6TeEXn6V6lEoZtM%3D&t=tea_iCZynPsFEqAq3Hg6v"
      />

      {/* Unify Intent tracking */}
      <Script
        id="unify-tracking"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `!function(){var e=["identify","page","startAutoPage","stopAutoPage","startAutoIdentify","stopAutoIdentify"];function t(o){return Object.assign([],e.reduce(function(r,n){return r[n]=function(){return o.push([n,[].slice.call(arguments)]),o},r},{}))}window.unify||(window.unify=t(window.unify)),window.unifyBrowser||(window.unifyBrowser=t(window.unifyBrowser));var n=document.createElement("script");n.async=!0,n.setAttribute("src","https://tag.unifyintent.com/v1/7xxj9W9Fia8Ghe1MY4CHcd/script.js"),n.setAttribute("data-api-key","wk_ShnoWZyk_4idgw2CHffpP8jwNGsvYZgoUFeJm1NQe"),n.setAttribute("id","unifytag"),(document.body||document.head).appendChild(n)}();`,
        }}
      />

      {/* RB2B visitor identity resolution */}
      <RB2BLoader />

      {/* Reddit Pixel */}
      <RedditPixel />
    </>
  );
}

export function TrackingScripts() {
  const { consent, isReady } = useConsent();

  if (!isReady) return null;

  return (
    <>
      {consent.analytics && <AnalyticsScripts />}
      {consent.marketing && <MarketingScripts />}
    </>
  );
}
